// src/components/Billing/BillingModal/index.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, CircularProgress, Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import MainBillingForm from "./MainBillingForm.js";
import {
  createBill,
  updateBill,
  getSingleBill,
  deleteBill, // ✅ NEW
} from "../../../services/billing/index.js";
import dayjs from "dayjs";
import { getDecoratorsDetails } from "../../../services/decorator/index.js";
import { getTaxes } from "../../../services/tax/index.js";
import { getAllOurAccounts, getShippingCouriers } from "../../../services/shipping/index.js";
import toast from "react-hot-toast";
import useFinanceCodes from "../../../hooks/useFinanceCodes";
import {
  filterFinanceCodesByScope,
  resolveFinanceCodeFromAlias,
} from "../../../services/financeCodes";
import {
  DEFAULT_BILLING_CONFIGURATION,
  normalizeBillingFinancialConfig,
} from "../../../constants/financialConfiguration";
import { getBillingModalInitializationKey } from "../billingModalContext";

// ✅ item-driven totals (keeps math consistent with list and table)
import { computeBillTotalsCents, fromCents } from "../../../utils/billingTotals";
import {
  billTaxInputFromCalculatedTax,
  getInitialBillTaxOverrideState,
  resolveBillTaxPayloadState,
  shouldSyncBillTaxInputToCalculated,
} from "./billTaxOverrideState";
import { BILLING_DIALOG_PAPER_SX } from "./billingModalLayout";
import { seedRemainingBillQuantities } from "./billRemainingQuantities";
import { includedBillingItems } from "./BillingItemsTable";
import {
  buildBillingSupplierMetadata,
  normalizeBillingSupplierSource,
  resolveBillingSupplierIdentity,
} from "./billingSupplierIdentity";
import { applyAuthoritativePoLineSubtotals } from "./billPrecision";
import {
  buildShippingBillingOptions,
  resolveShippingBillingIdentity,
} from "../../../utils/shippingBillingIdentity";

const toastWarning = (message) => toast(message, { icon: "⚠️" });

const hasValue = (value) => value !== null && value !== undefined && value !== "";
const createBlankBillFormData = () => ({
  supplier: "",
  decoratorId: "",
  shippingAccountId: "",
  shippingSelectionKey: "",
  billPartyType: "supplier",
  supplierName: "",
  shippingAccountName: "",
  relatedPoNumber: "",
  invoice: "",
  invoiceDate: dayjs(),
  dueDate: dayjs(),
  notes: "",
  defaultTax: "",
});
const normalizeTaxSelection = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  return value;
};
const roundMoney = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Number(num.toFixed(2)) : null;
};

const hasOwnValue = (source = {}, keys = []) =>
  keys.some((key) => {
    if (!Object.prototype.hasOwnProperty.call(source, key)) return false;
    const value = source[key];
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });

const hasIntentionalDecorationChargeData = (charge = {}) => {
  if (!charge || typeof charge !== "object") return false;
  const descriptiveFields = [
    "Name", "name", "title", "Title", "label", "Label",
    "description", "Description", "chargeName", "ChargeName",
    "method", "Method", "imprintMethod", "ImprintMethod",
    "location", "Location", "code", "Code", "chargeCode", "ChargeCode",
    "supplierChargeCode", "SupplierChargeCode", "type", "Type",
    "subType", "SubType", "chargeType", "ChargeType",
  ];
  const chargeIdFields = [
    "id", "Id", "chargeId", "ChargeId", "chargeID", "ChargeID",
    "charge_id", "supplierChargeId", "SupplierChargeId",
  ];
  const quantityFields = ["quantity", "Quantity", "qty", "Qty"];
  const financialFields = [
    "unitCost", "UnitCost", "supplierCost", "SupplierCost",
    "netCost", "NetCost", "cost", "Cost",
    "total", "Total", "amount", "Amount",
    "retail", "Retail", "price", "Price",
  ];
  const intentionalFlags = [
    "isSetupCharge", "IsSetupCharge", "setupCharge", "SetupCharge",
    "isRunCharge", "IsRunCharge", "runCharge", "RunCharge",
  ];

  return (
    hasOwnValue(charge, descriptiveFields) ||
    hasOwnValue(charge, chargeIdFields) ||
    hasOwnValue(charge, quantityFields) ||
    hasOwnValue(charge, financialFields) ||
    hasOwnValue(charge, intentionalFlags)
  );
};

const normalizeBillItemForForm = (item = {}) => {
  const taxOverrideAmount = hasValue(item?.taxOverrideAmount) ? roundMoney(item.taxOverrideAmount) : "";
  const persistedTaxAmount = hasValue(item?.taxAmount) ? roundMoney(item.taxAmount) : "";
  const calculatedTaxAmount = hasValue(item?.calculatedTaxAmount)
    ? roundMoney(item.calculatedTaxAmount)
    : (taxOverrideAmount === "" ? persistedTaxAmount : "");
  const taxAmount = taxOverrideAmount !== ""
    ? roundMoney(taxOverrideAmount)
    : (calculatedTaxAmount !== "" ? calculatedTaxAmount : persistedTaxAmount);

  return {
    ...item,
    taxAmount: taxAmount ?? "",
    taxOverrideAmount,
    calculatedTaxAmount,
  };
};

const serializeBillItemForSave = (item = {}, defaultTax = null) => {
  const normalized = normalizeBillItemForForm(item);
  const persistedItem = { ...normalized };
  delete persistedItem._variantDetails;
  delete persistedItem._included;
  return {
    ...persistedItem,
    tax: normalizeTaxSelection(normalized.tax) ?? normalizeTaxSelection(defaultTax),
    taxAmount: hasValue(normalized.taxAmount) ? roundMoney(normalized.taxAmount) : null,
    taxOverrideAmount: hasValue(normalized.taxOverrideAmount) ? roundMoney(normalized.taxOverrideAmount) : null,
    calculatedTaxAmount: hasValue(normalized.calculatedTaxAmount) ? roundMoney(normalized.calculatedTaxAmount) : null,
  };
};

const toPositiveInt = (value) => {
  const num = Number(value);
  return Number.isInteger(num) && num > 0 ? num : null;
};

const normalizeBillPartyType = (value, fallback = "supplier") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "decorator") return "decorator";
  if (normalized === "shipping-account") return "shipping-account";
  if (normalized === "other-service") return "other-service";
  return fallback;
};

const isMatchingBillSeed = (seed, poNumber) => {
  if (!seed) return false;
  if (seed.poNumber && String(seed.poNumber) === String(poNumber)) return true;
  return (
    !poNumber &&
    seed.billPartyType === "shipping-account" &&
    (
      seed.shippingAccountId != null ||
      seed.supplierInfo?.shippingAccountId != null ||
      seed.supplierInfo?.shippingAccount?.id != null
    )
  );
};

const getShippingAccountOptionName = (option) => option?.name ?? option?.accountName ?? option?.label ?? "";
const findDecoratorNameById = (options = [], value) => {
  if (!hasValue(value)) return "";
  const match = options.find((option) => String(option?.id) === String(value));
  return String(match?.name || "").trim();
};

const resolveBillSupplierMetadata = (
  currentBillSupplier,
  formData = {},
  {
    selectedSupplierObj = null,
    decoratorOptions = [],
    shippingAccountOptions = [],
    poDecoratorName = "",
    poShippingAccountName = "",
  } = {}
) => {
  const partyType = normalizeBillPartyType(formData?.billPartyType);

  if (partyType === "other-service") {
    const party = formData?.otherServiceParty || {};
    return { type: "other-service", sourceType: party.sourceType || null,
      sourceId: party.sourceId == null ? null : String(party.sourceId),
      Name: party.partyName || formData?.supplierName || "Other Service" };
  }

  if (partyType === "decorator") {
    const decoratorId = formData?.decoratorId || null;
    const decoratorName =
      findDecoratorNameById(decoratorOptions, decoratorId) ||
      String(formData?.decoratorLabel || poDecoratorName || "").trim() ||
      null;
    return decoratorName || decoratorId
      ? { type: "decorator", decoratorId: decoratorId || null, Name: decoratorName }
      : currentBillSupplier || null;
  }

  if (partyType === "shipping-account") {
    const selectionKey = formData?.shippingSelectionKey ||
      (formData?.shippingAccountId ? `shipping-account:${formData.shippingAccountId}` : "");
    const matched = shippingAccountOptions.find(
      (option) => String(option?.key) === String(selectionKey)
    );
    const identity = matched || resolveShippingBillingIdentity(currentBillSupplier);
    const shippingAccountId = identity?.sourceType === "shipping-account"
      ? identity.shippingAccountId
      : null;
    const accountName =
      identity?.accountName ||
      String(formData?.shippingAccountName || formData?.shippingAccountLabel || poShippingAccountName || "").trim() ||
      null;
    const courier = identity?.courier || null;
    return identity
      ? {
          type: "shipping-account",
          shippingAccountId: shippingAccountId || null,
          sourceType: identity.sourceType,
          ...(identity.sourceType === "courier" ? { sourceKey: identity.sourceKey } : { sourceId: identity.sourceId }),
          Name: identity.sourceType === "courier" ? courier : accountName,
          accountName,
          courier,
          accountNumber: identity.accountNumber || null,
          postalZipCode: identity.postalZipCode || null,
        }
      : currentBillSupplier || null;
  }

  const supplierId = formData?.supplier || null;
  const supplierName =
    selectedSupplierObj?.Name ||
    selectedSupplierObj?.name ||
    String(formData?.supplierName || formData?.supplierLabel || "").trim() ||
    null;
  return buildBillingSupplierMetadata({
    supplierId,
    supplierName,
    supplierSource: formData?.supplierSource,
    existing: currentBillSupplier,
  });
};

export const resolveMinimalBillEntryState = ({
  billingConfig,
  formData,
  poSeedData,
  selectedSupplierObj,
}) => {
  const minimalConfig = normalizeBillingFinancialConfig(billingConfig || DEFAULT_BILLING_CONFIGURATION).minimalBillEntry;
  const partyType = normalizeBillPartyType(formData?.billPartyType);
  const effectiveDecoratorId = partyType === "decorator"
    ? (
        toPositiveInt(formData?.decoratorId) ??
        toPositiveInt(poSeedData?.decoratorId) ??
        toPositiveInt(poSeedData?.supplierInfo?.decoratorId) ??
        null
      )
    : null;
  const effectiveSupplierId = partyType === "supplier"
    ? (
        toPositiveInt(formData?.supplier) ??
        toPositiveInt(selectedSupplierObj?.id) ??
        toPositiveInt(selectedSupplierObj?.value) ??
        toPositiveInt(selectedSupplierObj?.supplierId) ??
        toPositiveInt(selectedSupplierObj?.SupplierId) ??
        toPositiveInt(poSeedData?.supplierId) ??
        toPositiveInt(poSeedData?.supplierInfo?.supplierId) ??
        toPositiveInt(poSeedData?.supplierInfo?.vendorSupplierId) ??
        toPositiveInt(poSeedData?.supplierInfo?.id) ??
        null
      )
    : null;

  const matchedInternalDecorator = minimalConfig.enabled === true &&
    partyType === "decorator" &&
    effectiveDecoratorId != null &&
    minimalConfig.internalDecoratorIds.includes(effectiveDecoratorId);
  const matchedInternalSupplier = minimalConfig.enabled === true &&
    partyType === "supplier" &&
    effectiveSupplierId != null &&
    minimalConfig.internalSupplierIds.includes(effectiveSupplierId);

  const isActive = matchedInternalDecorator || matchedInternalSupplier;

  return {
    isActive,
    partyType,
    effectiveDecoratorId,
    effectiveSupplierId,
    matchedInternalDecorator,
    matchedInternalSupplier,
    hideVendorInvoiceFields: minimalConfig.hideVendorInvoiceFields !== false,
  };
};

const createValidationResult = () => ({ fieldErrors: {}, formErrors: [] });
const addValidationError = (result, field, message) => {
  if (field && !result.fieldErrors[field]) result.fieldErrors[field] = message;
  if (!result.formErrors.includes(message)) result.formErrors.push(message);
};
const hasValidationErrors = (result = {}) =>
  Object.keys(result.fieldErrors || {}).length > 0 || (result.formErrors || []).length > 0;

const validateBillPayload = ({ payLoad, isMinimalBillEntryActive }) => {
  const result = createValidationResult();
  const isDecoratorMode = payLoad.billPartyType === "decorator";
  const isShippingAccountMode = payLoad.billPartyType === "shipping-account";
  const isOtherServiceMode = payLoad.billPartyType === "other-service";

  if (!payLoad.OrderId) addValidationError(result, "OrderId", "Sales order is required.");
  if (!payLoad.poNumber && !isShippingAccountMode && !payLoad.isPoOptionalSupplier) {
    addValidationError(result, "poNumber", "PO number is required for supplier and decorator bills.");
  }
  if (isDecoratorMode && !toPositiveInt(payLoad.decoratorId)) {
    addValidationError(result, "decoratorId", "Select a bill decorator.");
  }
  if (isShippingAccountMode && payLoad?.billSupplier?.sourceType === "courier") {
    if (!String(payLoad?.billSupplier?.sourceKey || "").trim()) {
      addValidationError(result, "shippingSelectionKey", "Select a shipping provider or account.");
    }
  } else if (isShippingAccountMode && !toPositiveInt(payLoad.shippingAccountId)) {
    addValidationError(result, "shippingSelectionKey", "Select a shipping provider or account.");
  }
  if (!isDecoratorMode && !isShippingAccountMode && !isOtherServiceMode && !toPositiveInt(payLoad.supplierId)) {
    addValidationError(result, "supplier", "Select a bill supplier.");
  }
  if (payLoad.defaultTax === null || payLoad.defaultTax === undefined || String(payLoad.defaultTax).trim() === "") {
    addValidationError(result, "defaultTax", "Default tax is required.");
  }
  if (!Array.isArray(payLoad.billingItems) || payLoad.billingItems.length === 0) {
    addValidationError(result, "billingItems", "Add at least one bill item.");
  }

  if (!isMinimalBillEntryActive) {
    if (!String(payLoad.invoice || "").trim()) addValidationError(result, "invoice", "Invoice # is required.");
    if (!payLoad.invoiceDate) addValidationError(result, "invoiceDate", "Invoice date is required.");
    if (!payLoad.dueDate) addValidationError(result, "dueDate", "Due date is required.");
  }

  return result;
};

const normalizeServerValidation = (error) => {
  const data = error?.response?.data;
  const result = createValidationResult();
  if (Array.isArray(data?.errors)) {
    data.errors.forEach((entry) => addValidationError(result, entry?.field, entry?.message || data?.message || "Could not save bill."));
  } else if (data?.field && data?.message) {
    addValidationError(result, data.field, data.message);
  } else if (data?.message) {
    result.formErrors.push(data.message);
  }
  return result;
};

const getSaveFailureMessage = (error) => {
  if (!error?.response) {
    return "Could not reach the server. Check your connection and try again.";
  }
  return error?.response?.data?.message || "Could not save bill. Please try again.";
};

const BillingModal = ({
  fetchData,
  open,
  onClose,
  product,

  // from list
  poData,          // PO number
  poAmountData,    // PO Target (fallback)
  poStatusData,
  poSeedData,
  billOrderId,
  orderId,

  // ✅ these are already passed from Billing/index.jsx; destructure them
  poGroupBills = [],
  poComputedForPo = null,
  purchaseOrderOptions = [],
  billId = null, // current bill id when editing
}) => {
  const { tabId, instanceUniqueId } = useParams(); // bill id from route when editing
  const activeBillId = tabId || billId || null;
  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [billEntryModeLoading, setBillEntryModeLoading] = useState(true);
  const [minimalBillEntryCheckError, setMinimalBillEntryCheckError] = useState(null);
  const [taxOptions, setTaxOptions] = useState([]);
  const [decoratorOptions, setDecoratorOptions] = useState([]);
  const [shippingAccountOptions, setShippingAccountOptions] = useState([]);
  const [selectedSupplierObj, setSelectedSupplierObj] = useState(null);
  const [poOptionalSupplierStatus, setFreightSupplierStatus] = useState({ supplierId: null, isFreight: false, loading: false, error: false });
  const [billingConfig, setBillingConfig] = useState(DEFAULT_BILLING_CONFIGURATION);
  const [companyDefaultTaxId, setCompanyDefaultTaxId] = useState("");
  const {
    financeCodes = [],
    loading: financeCodesLoading = false,
  } = useFinanceCodes(instanceUniqueId, "bills") || {};

  // Editable line items
  const [singleBillData, setSingleBillData] = useState([
    // Can be empty by default
    // { code: "", itemName: "", quantity: 0, unitCost: 0, tax: "", amount: 0 }
  ]);
  const [billTaxInput, setBillTaxInput] = useState("");
  const [billTaxManuallyEdited, setBillTaxManuallyEdited] = useState(false);
  const [hasLegacyTaxDifference, setHasLegacyTaxDifference] = useState(false);
  const [validationResult, setValidationResult] = useState(createValidationResult);

  // Header fields
  const [formData, setFormData] = useState(createBlankBillFormData);
  const initializedContextRef = useRef(null);
  const lastSeededPoRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const authHeaders = useMemo(
    () => ({ headers: { accessToken: localStorage.getItem("accessToken") } }),
    []
  );

  const initializationKey = getBillingModalInitializationKey({
    billId: activeBillId,
    poNumber: poData,
    poSeedData,
  });

  useEffect(() => {
    if (!open || initializedContextRef.current === initializationKey) return;
    initializedContextRef.current = initializationKey;
    setBillEntryModeLoading(true);
    setMinimalBillEntryCheckError(null);
    lastSeededPoRef.current = null;
    setBillTaxInput("");
    setBillTaxManuallyEdited(false);
    setHasLegacyTaxDifference(false);
    setSelectedSupplierObj(null);
    setCompanyDefaultTaxId("");
    setValidationResult(createValidationResult());
    if (!activeBillId) {
      setFormData(createBlankBillFormData());
      setSingleBillData([]);
    }
  }, [open, activeBillId, initializationKey]);

  const clearFieldError = React.useCallback((field) => {
    if (!field) return;
    setValidationResult((prev) => {
      if (!prev?.fieldErrors?.[field]) return prev;
      const removedMessage = prev.fieldErrors[field];
      const nextFieldErrors = { ...prev.fieldErrors };
      delete nextFieldErrors[field];
      return {
        ...prev,
        fieldErrors: nextFieldErrors,
        formErrors: (prev.formErrors || []).filter((message) => message !== removedMessage),
      };
    });
  }, []);

  const handleSetSingleBillData = React.useCallback((updater) => {
    setSingleBillData(updater);
    clearFieldError("billingItems");
  }, [clearFieldError]);

  const scrollToFirstInvalidField = React.useCallback((result) => {
    const firstField = Object.keys(result?.fieldErrors || {})[0];
    if (!firstField || typeof document === "undefined") return;
    const target = document.querySelector(`[data-billing-field="${firstField}"]`);
    target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    const focusable = target?.querySelector?.("input, textarea, button, [tabindex]");
    focusable?.focus?.();
  }, []);

  useEffect(() => {
    if (!open || activeBillId) return;
    if (!poData) return;

    const seedMatchesPo = isMatchingBillSeed(poSeedData, poData);
    if (!seedMatchesPo && !billEntryModeLoading) {
      setBillEntryModeLoading(true);
    }
  }, [open, activeBillId, poData, poSeedData, billEntryModeLoading]);



  const findTaxOption = React.useCallback((rawTax) => {
    if (!rawTax || !Array.isArray(taxOptions) || taxOptions.length === 0) return null;

    const asNum = Number(rawTax);
    if (Number.isFinite(asNum)) {
      // tax id (e.g. 43)
      const byId = taxOptions.find((t) => Number(t?.id) === asNum);
      if (byId) return byId;

      // tax rate (e.g. 0.05) or numeric percent representation (e.g. 5 / "5.0000")
      const rateCandidates = [asNum];
      if (asNum > 1 && asNum <= 100) {
        rateCandidates.push(asNum / 100);
      }

      const byRate = taxOptions.find((t) =>
        rateCandidates.some((candidate) => Math.abs(Number(t?.value) - candidate) < 0.000001)
      );
      if (byRate) return byRate;
    }

    const asStr = String(rawTax).trim();
    if (!asStr) return null;

    // direct id-like string
    const byIdStr = taxOptions.find((t) => String(t?.id) === asStr);
    if (byIdStr) return byIdStr;

    // percentage text match e.g. "5%"
    const pctMatch = asStr.match(/([\d.]+)\s*%/);
    if (pctMatch) {
      const pct = Number(pctMatch[1]);
      if (Number.isFinite(pct)) {
        const rate = pct / 100;
        const byRateText = taxOptions.find((t) => Math.abs(Number(t?.value) - rate) < 0.000001);
        if (byRateText) return byRateText;
      }
    }

    // final loose name contains
    return taxOptions.find((t) => String(t?.name || '').toLowerCase().includes(asStr.toLowerCase())) || null;
  }, [taxOptions]);

  const resolveTaxForItem = React.useCallback((item, seed = null) => {
    const firstPricing = Array.isArray(item?.pricings) ? item.pricings[0] : null;
    const candidates = [
      firstPricing?.Taxes,
      firstPricing?.Tax,
      firstPricing?.tax,
      item?.Taxes,
      item?.Tax,
      item?.tax,
      seed?.tax,
      seed?.defaultTax,
    ];

    for (const c of candidates) {
      const opt = findTaxOption(c);
      if (opt) {
        return { taxId: opt.id, taxLabel: opt.name || '' };
      }
    }

    return { taxId: null, taxLabel: '' };
  }, [findTaxOption]);

  const mapPoItemsToBillingItems = React.useCallback((poItems = [], seed = null) => {
    const enabledBillCodes = filterFinanceCodesByScope(financeCodes, "bills");
    const serviceCode = resolveFinanceCodeFromAlias("Service", enabledBillCodes, "bills")?.code || "";

    const toCode = (raw = "", options = {}) => {
      const matched = resolveFinanceCodeFromAlias(raw, enabledBillCodes, "bills");
      if (matched?.code) return matched.code;
      if (options?.fallbackCode) return options.fallbackCode;
      if (serviceCode) {
        console.warn("[BillingModal] Unmapped finance charge name fell back to Service.", {
          rawValue: raw,
          instanceUniqueId,
          poNumber: seed?.poNumber || poData || null,
        });
        return serviceCode;
      }
      if (options?.allowRawValue) {
        const rawCode = String(raw || "").trim();
        if (rawCode) {
          console.warn("[BillingModal] Unmapped finance charge name preserved as legacy raw code.", {
            rawValue: rawCode,
            instanceUniqueId,
            poNumber: seed?.poNumber || poData || null,
          });
          return rawCode;
        }
      }
      console.warn("[BillingModal] Unmapped finance charge name produced an empty bill code.", {
        rawValue: raw,
        instanceUniqueId,
        poNumber: seed?.poNumber || poData || null,
      });
      return "";
    };
    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const inferTaxAmount = (src, baseAmount) => {
      const direct = [
        src?.taxAmount,
        src?.TaxAmount,
        src?.totalTax,
        src?.TotalTax,
      ].map(toNum).find((n) => n != null && n >= 0);
      if (direct != null) return Number(direct.toFixed(2));

      const total = [src?.amount, src?.Amount, src?.total, src?.Total].map(toNum).find((n) => n != null);
      if (total != null && Number.isFinite(baseAmount)) {
        const diff = total - baseAmount;
        if (diff > 0) return Number(diff.toFixed(2));
      }
      return "";
    };
    const splitTaxInclusive = ({ qty, unitCost, source }) => {
      const q = Number(qty) || 0;
      const u = Number(unitCost) || 0;
      const directTax = inferTaxAmount(source, q * u);
      if (directTax !== "" && Number(directTax) > 0) {
        return { unitCost: u, taxAmount: Number(directTax) };
      }
      return { unitCost: u, taxAmount: "" };
    };

    const out = [];
    let seenDefaultTaxId = null;
    let hasExplicitPricingRows = false;
    const supplierInfo = seed?.supplierInfo && typeof seed?.supplierInfo === "object"
      ? seed.supplierInfo
      : {};
    const poType = String(seed?.poType || supplierInfo?.type || "").toLowerCase();

    poItems.forEach((item) => {
      const taxResolved = resolveTaxForItem(item, seed);
      if (!seenDefaultTaxId && taxResolved?.taxId != null) {
        seenDefaultTaxId = taxResolved.taxId;
      }
      const pricingRowsRaw = Array.isArray(item?.pricings) ? item.pricings : [];
      const pricingRows = pricingRowsRaw.filter((pricingRow) => {
        const qty = Number(pricingRow?.Quantity ?? pricingRow?.quantity ?? item?.Quantity ?? item?.quantity ?? item?.qty ?? 0);
        const unit = Number(
          pricingRow?.NetCost ??
          pricingRow?.netCost ??
          pricingRow?.UnitCost ??
          pricingRow?.unitCost ??
          pricingRow?.Cost ??
          pricingRow?.cost ??
          pricingRow?.Price ??
          pricingRow?.price ??
          item?.supplierCost ??
          item?.SupplierCost ??
          item?.netCost ??
          item?.NetCost ??
          item?.Cost ??
          item?.cost ??
          item?.UnitCost ??
          item?.unitCost ??
          0
        );
        return Number.isFinite(qty) && qty > 0 && Number.isFinite(unit) && unit >= 0;
      });
      const qty = Number(item?.Quantity ?? item?.quantity ?? item?.qty ?? 0) || 0;
      const unitCost =
        Number(
          item?.supplierCost ??
            item?.SupplierCost ??
            item?.netCost ??
            item?.NetCost ??
            item?.Cost ??
            item?.cost ??
            item?.UnitCost ??
            item?.unitCost ??
            item?.pricings?.[0]?.NetCost ??
            item?.pricings?.[0]?.Cost ??
            item?.pricings?.[0]?.Price ??
            0
        ) || 0;

      // Decoration-only PO should not include supplier product goods rows.
      if (poType !== "different_supplier_decoration") {
        if (pricingRows.length > 0) {
          hasExplicitPricingRows = true;
          const baseName = item?.Name || item?.itemName || "Product";
          pricingRows.forEach((pricingRow) => {
            const pricingQty = Number(pricingRow?.Quantity ?? pricingRow?.quantity ?? item?.Quantity ?? item?.quantity ?? item?.qty ?? 0) || 0;
            if (!(pricingQty > 0)) return;
            const pricingUnitCost =
              Number(
                pricingRow?.NetCost ??
                pricingRow?.netCost ??
                pricingRow?.UnitCost ??
                pricingRow?.unitCost ??
                pricingRow?.Cost ??
                pricingRow?.cost ??
                pricingRow?.Price ??
                pricingRow?.price ??
                unitCost
              ) || 0;
            const skuBits = [
              pricingRow?.SKU ?? pricingRow?.sku,
              pricingRow?.Size ?? pricingRow?.size,
              pricingRow?.Color ?? pricingRow?.color,
            ].map((v) => String(v || "").trim()).filter(Boolean);
            const rowName = skuBits.length ? `${baseName} (${skuBits.join(" / ")})` : baseName;
            const pricingTaxResolved =
              findTaxOption(
                pricingRow?.Taxes
                ?? pricingRow?.Tax
                ?? pricingRow?.tax
                ?? taxResolved?.taxId
              )
              || null;
            if (!seenDefaultTaxId && pricingTaxResolved?.id != null) {
              seenDefaultTaxId = pricingTaxResolved.id;
            }
            const split = splitTaxInclusive({
              qty: pricingQty,
              unitCost: pricingUnitCost,
              source: pricingRow,
            });
            const sourceSubTotal = pricingQty * pricingUnitCost;
            out.push({
              code: toCode("Service"),
              itemName: rowName,
              _variantDetails: {
                style: pricingRow?.SKU ?? pricingRow?.sku ?? item?.styleNumber ?? item?.style,
                size: pricingRow?.Size ?? pricingRow?.size,
                color: pricingRow?.Color ?? pricingRow?.color ?? pricingRow?.Colour ?? pricingRow?.colour,
              },
              quantity: pricingQty,
              unitCost: split.unitCost,
              _poSourceSubTotal: sourceSubTotal,
              subTotal: Number(sourceSubTotal.toFixed(2)),
              tax: pricingTaxResolved?.id ?? taxResolved?.taxId ?? "",
              taxAmount: "",
            });
          });
        } else if (qty > 0) {
          const split = splitTaxInclusive({
            qty,
            unitCost,
            source: item,
          });
          const sourceSubTotal = qty * unitCost;
          out.push({
            code: toCode("Service"),
            itemName: item?.Name || item?.itemName || "Product",
            _variantDetails: {
              style: item?.styleNumber ?? item?.style ?? item?.SKU ?? item?.sku,
              size: item?.Size ?? item?.size,
              color: item?.Color ?? item?.color ?? item?.Colour ?? item?.colour,
            },
            quantity: qty,
            unitCost: split.unitCost,
            _poSourceSubTotal: sourceSubTotal,
            subTotal: Number(sourceSubTotal.toFixed(2)),
            tax: taxResolved?.taxId ?? "",
            taxAmount: split.taxAmount,
          });
        }
      }

      // Non-decoration POs should not pull decoration rows from product payload.
      if (poType === "no_decoration") {
        return;
      }

      const decorations = Array.isArray(item?.decorations) ? item.decorations : [];
      decorations.forEach((deco) => {
        const charges = Array.isArray(deco?.Charge)
          ? deco.Charge
          : Array.isArray(deco?.charges)
          ? deco.charges
          : [];

        charges.filter(hasIntentionalDecorationChargeData).forEach((charge) => {
          const cQty = Number(charge?.quantity ?? charge?.Quantity ?? charge?.qty ?? 1) || 1;
          const cUnit =
            Number(
              charge?.unitCost ??
                charge?.UnitCost ??
                charge?.supplierCost ??
                charge?.SupplierCost ??
                charge?.netCost ??
                charge?.NetCost ??
                charge?.cost ??
                charge?.Cost ??
                0
            ) || 0;
          const cName = charge?.title || charge?.Name || "Decoration Charge";
          const chargeTaxResolved =
            findTaxOption(
              charge?.taxId
              ?? charge?.taxes
              ?? charge?.Taxes
              ?? charge?.tax
              ?? charge?.Tax
              ?? taxResolved?.taxId
            ) || null;
          if (!seenDefaultTaxId && chargeTaxResolved?.id != null) {
            seenDefaultTaxId = chargeTaxResolved.id;
          }
          const split = splitTaxInclusive({
            qty: cQty,
            unitCost: cUnit,
            source: charge,
          });

          out.push({
            code: toCode(cName, { allowRawValue: true }),
            itemName: cName,
            quantity: cQty,
            unitCost: split.unitCost,
            subTotal: Number((cQty * split.unitCost).toFixed(2)),
            tax: chargeTaxResolved?.id ?? taxResolved?.taxId ?? "",
            taxAmount: chargeTaxResolved?.id != null ? "" : split.taxAmount,
          });
        });
      });
    });

    const supplierCharges = Array.isArray(supplierInfo?.charges) ? supplierInfo.charges : [];
    supplierCharges.filter(hasIntentionalDecorationChargeData).forEach((charge) => {
      const cQty = Number(charge?.quantity ?? charge?.Quantity ?? 1) || 1;
      const total = Number(charge?.Total ?? charge?.total ?? 0) || 0;
      const inferredUnit = cQty > 0 ? total / cQty : 0;
      const cUnit = Number(
        charge?.unitCost ??
        charge?.UnitCost ??
        charge?.supplierCost ??
        charge?.SupplierCost ??
        charge?.netCost ??
        charge?.NetCost ??
        inferredUnit
      ) || 0;
      const cName = charge?.Name || charge?.title || "Decoration Charge";
      const chargeTaxResolved =
        findTaxOption(
          charge?.taxId
          ?? charge?.taxes
          ?? charge?.Taxes
          ?? charge?.tax
          ?? charge?.Tax
          ?? seenDefaultTaxId
        ) || null;
      if (!seenDefaultTaxId && chargeTaxResolved?.id != null) {
        seenDefaultTaxId = chargeTaxResolved.id;
      }

      const exists = out.some((row) =>
        String(row?.code || "") === String(toCode(cName, { allowRawValue: true })) &&
        String(row?.itemName || "") === String(cName)
      );
      if (exists) return;
      const split = splitTaxInclusive({
        qty: cQty,
        unitCost: cUnit,
        source: charge,
      });

      out.push({
        code: toCode(cName, { allowRawValue: true }),
        itemName: cName,
        quantity: cQty,
        unitCost: split.unitCost,
        subTotal: Number((cQty * split.unitCost).toFixed(2)),
        tax: chargeTaxResolved?.id ?? seenDefaultTaxId ?? "",
        taxAmount: chargeTaxResolved?.id != null ? "" : split.taxAmount,
      });
    });

    return { items: applyAuthoritativePoLineSubtotals(out), defaultTaxId: seenDefaultTaxId, hasExplicitPricingRows };
  }, [financeCodes, findTaxOption, instanceUniqueId, poData, resolveTaxForItem]);

  const fetchSingleBillData = React.useCallback(async () => {
    const requestInitializationKey = initializationKey;
    try {
      let loadedShippingIdentity = null;
      setApiLoading(true);
      setBillEntryModeLoading(true);
      setMinimalBillEntryCheckError(null);
      if (activeBillId) {
        const data = await getSingleBill(activeBillId);
        if (initializedContextRef.current !== requestInitializationKey) return;
        loadedShippingIdentity = resolveShippingBillingIdentity(data);
        const normalizedBillPartyType = normalizeBillPartyType(
          data?.billPartyType || data?.billSupplier?.type,
          data?.decoratorId != null ? "decorator" : "supplier"
        );
        const supplierIdentity = resolveBillingSupplierIdentity({ bill: data });
        const billSupplierName = supplierIdentity.supplierName;
        const billSupplierSource = supplierIdentity.supplierSource;

        setFormData({
          supplier:
            supplierIdentity.supplierId ||
            data?.supplierId ||
            "",
          decoratorId: data?.decoratorId || "",
          shippingAccountId: normalizedBillPartyType === "shipping-account"
            ? String(data?.billSupplier?.shippingAccountId || data?.billSupplier?.id || "") : "",
          shippingSelectionKey: normalizedBillPartyType === "shipping-account"
            ? (loadedShippingIdentity?.key || "") : "",
          billPartyType: normalizedBillPartyType,
          billSupplier: data?.billSupplier || null,
          supplierSource: billSupplierSource,
          supplierName: billSupplierName,
          shippingAccountName: String(
            data?.billSupplier?.accountName ||
            data?.billSupplier?.Name ||
            ""
          ).trim(),
          relatedPoNumber:
            normalizedBillPartyType === "shipping-account"
              ? String(data?.poNumber || "")
              : "",
          invoice: data?.invoice || "",
          invoiceDate: dayjs(data?.invoiceDate || dayjs()),
          dueDate: dayjs(data?.dueDate || dayjs()),
          notes: data?.notes || "",
          defaultTax: data?.defaultTax || "",
        });

        setSingleBillData(
          Array.isArray(data?.billingItems)
            ? data.billingItems.map(normalizeBillItemForForm)
            : []
        );
        const initialBillTaxState = getInitialBillTaxOverrideState({
          bill: data,
          calculatedTaxCents: computeBillTotalsCents({ billingItems: data?.billingItems || [] }).taxCents,
        });
        setBillTaxInput(initialBillTaxState.billTaxInput);
        setBillTaxManuallyEdited(initialBillTaxState.billTaxManuallyEdited);
        setHasLegacyTaxDifference(initialBillTaxState.hasLegacyTaxDifference);
        setSelectedSupplierObj(
          normalizedBillPartyType === "supplier"
            ? supplierIdentity.selectedSupplierOption
            : null
        );
      }

      const [decoratorsResponse, taxesData, shippingAccountsResponse, couriersResponse] = await Promise.all([
        getDecoratorsDetails(instanceUniqueId),
        getTaxes(),
        getAllOurAccounts({ instanceUniqueId }).catch(() => ({ data: [] })),
        getShippingCouriers().catch(() => ({ couriers: [] })),
      ]);
      if (initializedContextRef.current !== requestInitializationKey) return;
      setTaxOptions(taxesData?.taxes || []);
      const resolvedShippingOptions = buildShippingBillingOptions(
        shippingAccountsResponse?.data || [],
        couriersResponse?.couriers || []
      );
      if (loadedShippingIdentity && !resolvedShippingOptions.some(({ key }) => key === loadedShippingIdentity.key)) {
        resolvedShippingOptions.push(loadedShippingIdentity);
      }
      setShippingAccountOptions(resolvedShippingOptions);
      const decorators = Array.isArray(decoratorsResponse?.result) ? decoratorsResponse.result : [];
      setDecoratorOptions(
        decorators.map((decorator) => ({
          id: String(decorator?.id ?? ""),
          name: String(
            decorator?.title ||
            decorator?.Description ||
            decorator?.name ||
            decorator?.Name ||
            ""
          ).trim() || `Decorator ${decorator?.id}`,
        })).filter((option) => option.id)
      );

      if (instanceUniqueId) {
        try {
          const instanceRes = await axios.get(
            `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
            authHeaders
          );
          const targetInstanceId = instanceRes.data?.id;
          if (targetInstanceId) {
            const defaultsRes = await axios.get(
              `${SERVER}/instances/${targetInstanceId}/defaults`,
              authHeaders
            );
            if (initializedContextRef.current !== requestInitializationKey) return;
            const instanceDefaultTaxId = normalizeTaxSelection(defaultsRes.data?.defaults?.defaultTaxId) ?? "";
            setCompanyDefaultTaxId(instanceDefaultTaxId);
            if (!activeBillId && instanceDefaultTaxId !== "") {
              setFormData((prev) => ({
                ...prev,
                defaultTax: normalizeTaxSelection(prev.defaultTax) ?? instanceDefaultTaxId,
              }));
            }
            setBillingConfig(
              normalizeBillingFinancialConfig(
                defaultsRes.data?.defaults?.financialConfiguration?.billing || {}
              )
            );
          } else {
            setCompanyDefaultTaxId("");
            setBillingConfig(DEFAULT_BILLING_CONFIGURATION);
          }
        } catch (configError) {
          console.warn("Unable to load billing financial configuration. Falling back to normal bill entry mode.", configError);
          setCompanyDefaultTaxId("");
          setBillingConfig(DEFAULT_BILLING_CONFIGURATION);
          setMinimalBillEntryCheckError(configError);
          toastWarning("Unable to load Financial Configuration. Falling back to normal bill entry mode.");
        }
      }

      if (activeBillId) {
        setBillEntryModeLoading(false);
      }
      setApiLoading(false);
    } catch (err) {
      console.error("Error fetching bill:", err);
      setBillingConfig(DEFAULT_BILLING_CONFIGURATION);
      setMinimalBillEntryCheckError(err);
      toastWarning("Unable to finish checking bill entry mode. Falling back to normal bill entry mode.");
      setBillEntryModeLoading(false);
      setApiLoading(false);
    }
  }, [SERVER, activeBillId, authHeaders, instanceUniqueId, initializationKey]);

  useEffect(() => {
    if (!open) return;
    fetchSingleBillData();
  }, [fetchSingleBillData, open]);

  useEffect(() => {
    if (activeBillId) return;
    if (financeCodesLoading) return;
    const seed = poSeedData || null;
    if (!isMatchingBillSeed(seed, poData)) return;

    const seedKey = `${initializationKey}|tax:${Array.isArray(taxOptions) ? taxOptions.length : 0}|dec:${Array.isArray(decoratorOptions) ? decoratorOptions.length : 0}|fc:${Array.isArray(financeCodes) ? financeCodes.length : 0}`;
    if (lastSeededPoRef.current === seedKey) return;

    const mapped = mapPoItemsToBillingItems(seed.items || [], seed);
    const defaultTaxId =
      mapped?.defaultTaxId ??
      formData.defaultTax ??
      companyDefaultTaxId ??
      (Array.isArray(taxOptions) && taxOptions.length ? taxOptions[0]?.id : "");
    const mappedItems = Array.isArray(mapped?.items) ? mapped.items : [];
    const seededItems = applyAuthoritativePoLineSubtotals(
      seedRemainingBillQuantities(mappedItems, poGroupBills)
    );
    setSingleBillData(seededItems);
    lastSeededPoRef.current = seedKey;

    if (!formData.defaultTax) {
      const nextDefaultTax = defaultTaxId;
      if (nextDefaultTax != null && nextDefaultTax !== "") {
        setFormData((prev) => ({ ...prev, defaultTax: nextDefaultTax }));
      }
    }

    if (decoratorOptions?.length || shippingAccountOptions?.length || seed?.supplierId || seed?.supplierInfo) {
      const seededSupplierId =
        seed?.supplierId ??
        seed?.supplierInfo?.supplierId ??
        seed?.supplierInfo?.vendorSupplierId ??
        seed?.supplierInfo?.id ??
        null;
      const seededDecoratorId =
        seed?.decoratorId ??
        seed?.decoratorInfo?.id ??
        seed?.supplierInfo?.decoratorId ??
        null;
      const seededShippingAccountId =
        seed?.shippingAccountId ??
        seed?.supplierInfo?.shippingAccountId ??
        seed?.supplierInfo?.shippingAccount?.id ??
        null;
      const seededShippingAccountName = String(
        seed?.shippingAccount?.accountName ||
        seed?.supplierInfo?.shippingAccount?.accountName ||
        seed?.supplierInfo?.shippingAccount?.name ||
        ""
      ).trim();
      const seededSupplierName = String(
        seed?.supplierInfo?.supplierName ||
        seed?.supplierName ||
        ''
      ).trim();
      const seededIdentity = resolveBillingSupplierIdentity({
        bill: { supplierId: seededSupplierId, billSupplier: { Name: seededSupplierName } },
        po: seed,
      });
      if (seed?.billPartyType === "other-service" && seed?.supplierInfo?.party?.sourceType && seed?.supplierInfo?.party?.sourceId) {
        setFormData((prev) => ({
          ...prev,
          billPartyType: "other-service",
          supplierName: seed.supplierInfo.party.partyName || seededSupplierName,
          otherServiceParty: seed.supplierInfo.party,
        }));
      } else if (seed?.billPartyType === "shipping-account" && seededShippingAccountId != null) {
        setFormData((prev) => ({
          ...prev,
          billPartyType: "shipping-account",
          shippingAccountId: String(seededShippingAccountId),
          shippingSelectionKey: `shipping-account:${seededShippingAccountId}`,
          shippingAccountName: seededShippingAccountName || prev.shippingAccountName || "",
        }));
      } else if (seededIdentity.supplierId && seed?.billPartyType !== "decorator" && seededDecoratorId == null) {
        setSelectedSupplierObj(seededIdentity.selectedSupplierOption);
        setFormData((prev) => ({
          ...prev,
          supplier: seededIdentity.supplierId,
          supplierSource: seededIdentity.supplierSource,
          supplierName: seededIdentity.supplierName || prev.supplierName || '',
        }));
      } else {
        if (seededDecoratorId != null && Array.isArray(decoratorOptions) && decoratorOptions.length) {
          const decoratorMatched = decoratorOptions.find(
            (option) => String(option?.id) === String(seededDecoratorId)
          );
          if (decoratorMatched?.id != null && decoratorMatched?.id !== "") {
            setFormData((prev) => ({
              ...prev,
              billPartyType: "decorator",
              decoratorId: String(decoratorMatched.id),
            }));
          }
        } else if (seededShippingAccountId != null) {
          setFormData((prev) => ({
            ...prev,
            billPartyType: "shipping-account",
            shippingAccountId: String(seededShippingAccountId),
            shippingSelectionKey: `shipping-account:${seededShippingAccountId}`,
            shippingAccountName: seededShippingAccountName || prev.shippingAccountName || "",
          }));
        } else if (seededIdentity.supplierId && seededIdentity.supplierName) {
          setSelectedSupplierObj(seededIdentity.selectedSupplierOption);
          setFormData((prev) => ({
            ...prev,
            billPartyType: "supplier",
            supplier: seededIdentity.supplierId,
            supplierSource: seededIdentity.supplierSource,
            supplierName: seededIdentity.supplierName,
          }));
        }
      }
    }
    setBillEntryModeLoading(false);
  }, [activeBillId, poData, poSeedData, decoratorOptions, shippingAccountOptions, taxOptions, formData.defaultTax, companyDefaultTaxId, poAmountData, financeCodes, financeCodesLoading, mapPoItemsToBillingItems, poGroupBills, initializationKey]);

  useEffect(() => {
    if (!open || activeBillId) return;
    if (apiLoading || financeCodesLoading) return;

    const expectsPoSeed = initializationKey.startsWith("po:");
    if (!expectsPoSeed) {
      if (billEntryModeLoading) {
        setBillEntryModeLoading(false);
      }
      return;
    }

    const seed = poSeedData || null;
    const seedMatchesPo = isMatchingBillSeed(seed, poData);
    if (seedMatchesPo) return;

    if (billEntryModeLoading) {
      console.warn("Unable to resolve PO seed data for bill entry mode. Falling back to normal bill entry mode.", {
        poNumber: poData,
        poSeedData: seed,
      });
      setMinimalBillEntryCheckError(new Error("Unable to resolve PO seed data for bill entry mode."));
      toastWarning("Unable to finish checking bill entry mode. Falling back to normal bill entry mode.");
      setBillEntryModeLoading(false);
    }
  }, [open, activeBillId, apiLoading, financeCodesLoading, poSeedData, poData, billEntryModeLoading, initializationKey]);

  // ✅ live actual billed from the editable line items (memoized)
  const totalsC = useMemo(
    () => computeBillTotalsCents({ billingItems: includedBillingItems(singleBillData) }, { taxOptions }),
    [singleBillData, taxOptions]
  );
  const isCreditMemo = totalsC.subTotalCents < 0;
  const billTaxState = resolveBillTaxPayloadState({
    billTaxInput,
    calculatedTaxCents: totalsC.taxCents,
    billTaxManuallyEdited,
    hasLegacyTaxDifference,
    allowNegative: isCreditMemo,
  });
  const billTaxIsValid = billTaxState.billTaxIsValid;
  const effectiveTaxCents = billTaxState.effectiveTaxCents;
  const actualBilled = fromCents(totalsC.subTotalCents + effectiveTaxCents);

  useEffect(() => {
    if (!open) return;
    if (!shouldSyncBillTaxInputToCalculated({ billTaxManuallyEdited, hasLegacyTaxDifference })) return;
    setBillTaxInput(billTaxInputFromCalculatedTax(totalsC.taxCents));
  }, [open, billTaxManuallyEdited, hasLegacyTaxDifference, totalsC.taxCents]);

  const handleBillTaxInputChange = (value) => {
    setBillTaxManuallyEdited(true);
    setHasLegacyTaxDifference(false);
    setBillTaxInput(value);
  };

  const handleResetBillTaxInput = () => {
    setBillTaxInput(fromCents(totalsC.taxCents).toFixed(2));
    setBillTaxManuallyEdited(false);
    setHasLegacyTaxDifference(false);
  };

  // Prefer the computed PO target for this PO group; fallback to row value
  const targetPoAmount = Number(
    (poComputedForPo && poComputedForPo.target) != null
      ? poComputedForPo.target
      : poAmountData
  ) || 0;

  // Best-guess PO supplier label for comparisons in the form
  const poSupplierName = useMemo(() => {
    const first = Array.isArray(poGroupBills) ? poGroupBills[0] : null;
    return (
      poSeedData?.supplierInfo?.supplierName ||
      first?._poSupplierInfo?.supplierName ||
      first?.supplierInfo ||
      first?.billSupplierInfo?.Name ||
      ""
    );
  }, [poGroupBills, poSeedData]);

  const poDecoratorName = useMemo(() => {
    const first = Array.isArray(poGroupBills) ? poGroupBills[0] : null;
    const poDecoratorId =
      poSeedData?.decoratorId ??
      poSeedData?.supplierInfo?.decoratorId ??
      first?._poSupplierInfo?.decoratorId ??
      null;

    if (poDecoratorId != null && Array.isArray(decoratorOptions) && decoratorOptions.length) {
      const matched = decoratorOptions.find((option) => String(option?.id) === String(poDecoratorId));
      if (matched?.name) return matched.name;
    }

    return (
      poSeedData?.supplierInfo?.decoratorName ||
      poSeedData?.supplierInfo?.decoratorTitle ||
      first?._poSupplierInfo?.decoratorName ||
      first?._poSupplierInfo?.decoratorTitle ||
      ""
    );
  }, [poGroupBills, poSeedData, decoratorOptions]);

  const poShippingAccountName = useMemo(() => {
    const first = Array.isArray(poGroupBills) ? poGroupBills[0] : null;
    return (
      poSeedData?.shippingAccount?.accountName ||
      poSeedData?.supplierInfo?.shippingAccount?.accountName ||
      first?._poSupplierInfo?.shippingAccount?.accountName ||
      first?._poSupplierInfo?.shippingAccount?.name ||
      ""
    );
  }, [poGroupBills, poSeedData]);

  const minimalBillEntryState = useMemo(
    () => resolveMinimalBillEntryState({ billingConfig, formData, poSeedData, selectedSupplierObj }),
    [billingConfig, formData, poSeedData, selectedSupplierObj]
  );
  const minimalBillEntryMessage = minimalBillEntryState.matchedInternalSupplier
    ? "Minimal bill entry is enabled because this bill is assigned to a configured internal supplier. Vendor invoice details are not required."
    : minimalBillEntryState.matchedInternalDecorator
      ? "Minimal bill entry is enabled because this bill is assigned to a configured internal decorator. Vendor invoice details are not required."
      : "";
  const hasMinimalBillEntryCheckError = Boolean(minimalBillEntryCheckError);

  // 🔧 Make handleSelect accept both shapes:
  //  1) (event, name)  -> from CustomDropdown onChange
  //  2) (name, value)  -> from our "Use PO Supplier" convenience action
  const handleSelect = (a, b) => {
    // case (name, value)
    if (typeof a === "string") {
      const name = a;
      const value = b;
      if (name === "shippingSelectionKey") {
        const matched = shippingAccountOptions.find((option) => String(option?.key) === String(value || ""));
        setFormData((prev) => ({
          ...prev,
          shippingSelectionKey: value,
          shippingAccountId: matched?.sourceType === "shipping-account" ? String(matched.shippingAccountId) : "",
          shippingAccountName: getShippingAccountOptionName(matched) || "",
        }));
        clearFieldError("shippingSelectionKey");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(name === "billPartyType" ? { relatedPoNumber: "" } : {}),
      }));
      clearFieldError(name === "relatedPoNumber" ? "poNumber" : name);
      return;
    }
    // case (event, name)
    const e = a;
    const name = b;
    if (name === "shippingSelectionKey") {
      const value = e?.target?.value ?? e;
      const matched = shippingAccountOptions.find(
        (option) => String(option?.key) === String(value || "")
      );
      setFormData((prev) => ({
        ...prev,
        shippingSelectionKey: value,
        shippingAccountId: matched?.sourceType === "shipping-account" ? String(matched.shippingAccountId) : "",
        shippingAccountName: getShippingAccountOptionName(matched) || prev.shippingAccountName || "",
      }));
      clearFieldError("shippingSelectionKey");
      return;
    }
    const value = e?.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name === "relatedPoNumber" ? "poNumber" : name);
  };

  // The Freight/Service lookup is for instance-owned custom CoreSuppliers only.
  // ASI catalogue suppliers are always PO-backed, even when their numeric IDs
  // overlap a CoreSupplier or a verified local ASI backup.
  // Source-less legacy bills retain the existing lookup until reselected.
  const isExplicitAsiSupplier =
    normalizeBillingSupplierSource(formData.supplierSource) === "asi-supplier";

  useEffect(() => {
    const supplierId = Number(formData.supplier || selectedSupplierObj?.Id || selectedSupplierObj?.id);
    if (!open || formData.billPartyType !== 'supplier' || isExplicitAsiSupplier || !Number.isSafeInteger(supplierId) || supplierId <= 0) {
      setFreightSupplierStatus({ supplierId: null, isFreight: false, loading: false, error: false });
      return;
    }
    let active = true;
    setFreightSupplierStatus({ supplierId, isFreight: false, loading: true, error: false });
    axios.get(`${SERVER}/suppliers/${supplierId}/classification`, {
      params: { instanceUniqueId }, ...authHeaders,
    }).then(({ data }) => {
      if (active) setFreightSupplierStatus({ supplierId, isFreight: ['freight', 'service'].includes(data?.supplierType), loading: false, error: false });
    }).catch(() => {
      if (active) setFreightSupplierStatus({ supplierId, isFreight: false, loading: false, error: true });
    });
    return () => { active = false; };
  }, [open, formData.supplier, formData.billPartyType, selectedSupplierObj?.Id, selectedSupplierObj?.id, isExplicitAsiSupplier, instanceUniqueId, SERVER, authHeaders]);

  const handleSupplierSelect = (supplier) => {
    const supplierId = supplier?.id ?? supplier?.Id ?? "";
    const supplierName = String(supplier?.Name ?? supplier?.name ?? "").trim();
    const supplierSource = normalizeBillingSupplierSource(supplier?.supplierSource ?? supplier?.SupplierSource);
    setSelectedSupplierObj(supplier || null);
    setFormData((prev) => ({
      ...prev,
      supplier: supplierId ? String(supplierId) : "",
      supplierSource,
      supplierName: supplierName || (supplierId ? prev.supplierName : ""),
    }));
    clearFieldError("supplier");
  };

  const handleSubmit = async () => {
    try {
      if (loading || apiLoading || saveInFlightRef.current) return;
      if (billEntryModeLoading) {
        return;
      }
      saveInFlightRef.current = true;
      setLoading(true);
      // Always compute from current items at submit time
      const includedItems = includedBillingItems(singleBillData);
      const t = computeBillTotalsCents({ billingItems: includedItems }, { taxOptions });
      if (!billTaxIsValid) {
        const message = isCreditMemo
          ? "Bill Tax must be a valid currency value."
          : "Bill Tax must be a non-negative currency value.";
        const nextValidation = createValidationResult();
        addValidationError(nextValidation, "billTax", message);
        setValidationResult(nextValidation);
        toast.error(message);
        setLoading(false);
        return;
      }
      const submitTaxState = resolveBillTaxPayloadState({
        billTaxInput,
        calculatedTaxCents: t.taxCents,
        billTaxManuallyEdited,
        hasLegacyTaxDifference,
        allowNegative: t.subTotalCents < 0,
      });
      const effectiveTaxCents = submitTaxState.effectiveTaxCents;
      const isTaxOverridden = submitTaxState.isTaxOverridden;

      const fallbackSupplierId =
        formData.supplier ||
        poSeedData?.supplierId ||
        poSeedData?.supplierInfo?.supplierId ||
        poSeedData?.supplierInfo?.vendorSupplierId ||
        poSeedData?.supplierInfo?.id ||
        "";
      const fallbackDecoratorId =
        formData.decoratorId ||
        poSeedData?.decoratorId ||
        poSeedData?.supplierInfo?.decoratorId ||
        "";
      const partyType = normalizeBillPartyType(formData.billPartyType);
      const isDecoratorMode = partyType === "decorator";
      const isShippingAccountMode = partyType === "shipping-account";
      const isOtherServiceMode = partyType === "other-service";
      const billSupplier = resolveBillSupplierMetadata(
        formData.billSupplier || null,
        formData,
        {
          selectedSupplierObj,
          decoratorOptions,
          shippingAccountOptions,
          poDecoratorName,
          poShippingAccountName,
        }
      );
      const fallbackShippingAccountId = billSupplier?.sourceType === "courier"
        ? null
        : (formData.shippingAccountId || poSeedData?.shippingAccountId ||
          poSeedData?.supplierInfo?.shippingAccountId || poSeedData?.supplierInfo?.shippingAccount?.id || null);

      const isPoOptionalSelected = partyType === 'supplier' && !isExplicitAsiSupplier &&
        Number(poOptionalSupplierStatus.supplierId) === Number(fallbackSupplierId) &&
        poOptionalSupplierStatus.isFreight;
      if (partyType === 'supplier' && !isExplicitAsiSupplier && (poOptionalSupplierStatus.loading || poOptionalSupplierStatus.error)) {
        if (poOptionalSupplierStatus.error) {
          setValidationResult({ fieldErrors: { supplier: 'Unable to verify supplier type. Please reselect the supplier.' }, formErrors: ['Unable to verify supplier type. Please reselect the supplier.'] });
        }
        setLoading(false);
        saveInFlightRef.current = false;
        return;
      }
      const payLoad = {
        poNumber: isShippingAccountMode
          ? (String(formData.relatedPoNumber || "").trim() || null)
          : (poData || (isPoOptionalSelected ? null : (String(formData.relatedPoNumber || "").trim() || null))),
        isPoOptionalSupplier: isPoOptionalSelected,
        poStatus: poStatusData,
        OrderId: billOrderId,
        poAmount: poAmountData, // legacy target snapshot; PO-backed display uses canonical PO context when available
        supplierId: isDecoratorMode || isShippingAccountMode || isOtherServiceMode ? null : (fallbackSupplierId || null),
        decoratorId: isDecoratorMode ? (fallbackDecoratorId || null) : null,
        shippingAccountId: isShippingAccountMode ? (fallbackShippingAccountId || null) : null,
        billPartyType: partyType,
        sourceType: isOtherServiceMode ? formData?.otherServiceParty?.sourceType : null,
        sourceId: isOtherServiceMode ? formData?.otherServiceParty?.sourceId : null,
        billSupplier,
        invoice: String(formData.invoice || "").trim(),
        invoiceDate: formData.invoiceDate?.format ? formData.invoiceDate.format("YYYY-MM-DD") : formData.invoiceDate,
        dueDate: formData.dueDate?.format ? formData.dueDate.format("YYYY-MM-DD") : formData.dueDate,
        notes: formData.notes,
        defaultTax: formData.defaultTax,
        instanceUniqueId,

        // ✅ authoritative totals from items
        subTotal: fromCents(t.subTotalCents),
        calculatedTotalTax: fromCents(t.taxCents),
        taxOverrideAmount: submitTaxState.taxOverrideAmount,
        isTaxOverridden,
        totalTax: submitTaxState.totalTax,
        amount: fromCents(t.subTotalCents + effectiveTaxCents),
        amountDue: fromCents(t.subTotalCents + effectiveTaxCents),

        billingItems: includedItems.map((item) => serializeBillItemForSave(item, formData.defaultTax)),
      };

      const validationErrors = validateBillPayload({
        payLoad,
        isMinimalBillEntryActive: minimalBillEntryState.isActive,
      });
      if (hasValidationErrors(validationErrors)) {
        setValidationResult(validationErrors);
        if (process.env.NODE_ENV === "development") {
          console.warn("[BillingModal] Save blocked by validation", {
            fields: Object.keys(validationErrors.fieldErrors || {}),
            billPartyType: payLoad.billPartyType,
            hasPoNumber: Boolean(payLoad.poNumber),
            hasOrderId: Boolean(payLoad.OrderId),
          });
        }
        toast.error(validationErrors.formErrors[0] || "Unable to save this bill.");
        scrollToFirstInvalidField(validationErrors);
        setLoading(false);
        return;
      }
      setValidationResult(createValidationResult());

      const saveBillId = activeBillId || billId || null;

      if (saveBillId) {
        await updateBill(saveBillId, payLoad);
      } else {
        await createBill(payLoad);
      }

      onClose();
      await fetchData();
    } catch (err) {
      console.error("Error in handleSubmit:", err);
      const serverValidation = normalizeServerValidation(err);
      if (hasValidationErrors(serverValidation)) {
        setValidationResult(serverValidation);
      }
      toast.error(getSaveFailureMessage(err));
    } finally {
      saveInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const id = activeBillId;
      if (!id) return;
      const ok = window.confirm("Delete this bill? This cannot be undone.");
      if (!ok) return;
      setLoading(true);
      await deleteBill(id);
      toast.success("Bill deleted");
      onClose();
      await fetchData();
    } catch (err) {
      console.error("Delete bill failed:", err);
      toast.error(err?.response?.data?.message || "Failed to delete bill");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (newDate, name) => {
    setFormData((prev) => ({ ...prev, [name]: newDate }));
    clearFieldError(name);
  };

  const handleInputChange = (event, name) => {
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: BILLING_DIALOG_PAPER_SX }}
    >
      <DialogTitle className="create-order-dialog-title">
        {activeBillId
          ? `Edit Bill${String(formData.invoice || "").trim() ? ` — ${String(formData.invoice).trim()}` : ""}`
          : "Add Bill"}
      </DialogTitle>

      <DialogContent
        className="create-order-dialog-content"
        aria-busy={billEntryModeLoading}
        data-bill-entry-check-error={hasMinimalBillEntryCheckError ? "true" : "false"}
      >
        {billEntryModeLoading ? (
          <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Checking bill entry mode…
            </Typography>
          </Box>
        ) : (
          <>
            {hasValidationErrors(validationResult) ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Unable to save this bill</Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {validationResult.formErrors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </Box>
              </Alert>
            ) : null}
            <MainBillingForm
              financeCodes={financeCodes}
              singleBillData={singleBillData}
              setSingleBillData={handleSetSingleBillData}
              formData={formData}
              fieldErrors={validationResult.fieldErrors}
              handleSelect={handleSelect}
              handleDateChange={handleDateChange}
              handleInputChange={handleInputChange}
              selectedSupplierObj={selectedSupplierObj}
              onSelectSupplier={handleSupplierSelect}
              decoratorOptions={decoratorOptions}
              shippingAccountOptions={shippingAccountOptions}
              purchaseOrderOptions={purchaseOrderOptions}
              isPoOptionalSupplier={formData.billPartyType === 'supplier' && poOptionalSupplierStatus.isFreight}
              poOptionalSupplierLoading={formData.billPartyType === 'supplier' && poOptionalSupplierStatus.loading}
              taxOptions={taxOptions}
              instanceUniqueId={instanceUniqueId}
              poAmount={targetPoAmount}
              // we still pass "this bill" live amount for the right panel, but PO totals will be computed in-form
              actualBilled={Number(actualBilled) || 0}
              poGroupBills={poGroupBills}
              currentBillId={activeBillId}
              poSupplierName={poSupplierName}
              poDecoratorName={poDecoratorName}
              poShippingAccountName={poShippingAccountName}
              poNumber={poData}
              isDecoratorPo={false}
              isMinimalBillEntryActive={minimalBillEntryState.isActive && minimalBillEntryState.hideVendorInvoiceFields}
              minimalBillEntryMessage={minimalBillEntryMessage}
              billTaxInput={billTaxInput}
              setBillTaxInput={setBillTaxInput}
              onBillTaxInputChange={(value) => {
                handleBillTaxInputChange(value);
                clearFieldError("billTax");
              }}
              onResetBillTaxInput={handleResetBillTaxInput}
              billTaxManuallyEdited={billTaxManuallyEdited}
              hasLegacyTaxDifference={hasLegacyTaxDifference}
              clearLegacyTaxDifference={() => setHasLegacyTaxDifference(false)}
              allowNegativeBillTax={isCreditMemo}
              allowDetailedItemTaxCustomization={billingConfig.allowDetailedItemTaxCustomization !== false}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        {activeBillId && (
          <Button
            onClick={handleDelete}
            color="error"
            variant="outlined"
            disabled={loading || apiLoading || billEntryModeLoading}
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose} color="secondary" disabled={loading || apiLoading}>
          Cancel
        </Button>
        <Button
          sx={{ backgroundColor: "#c0181f !important", color: "white !important", ml: "5px !important" }}
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || apiLoading || billEntryModeLoading}
        >
          {billEntryModeLoading ? "Checking..." : loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingModal;
